import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format, parse } from 'date-fns';
import Breadcrumbs from "../components/breadcrumbs/Breadcrumbs";
import checkIcon from "../assets/icons/green-circle-check.svg";
import api from "../api/api";
import Laoder from "../components/loader/Loader";
import nfcPayment from "../assets/icons/nfc-payment.svg";
import "../assets/css/success.css";
import Header from "../components/nav/Header";

export default function PaymentConfirmation() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const bookingId = searchParams.get("booking_id");

  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);

    if (bookingId && !error) {
      const res = await api.paymentInformation({
        booking_id: bookingId,
        completed: "success",
        error,
      });

      if (res?.status === 200) {
        setFailed(false);
        setPaymentData(res.data);
      } else {
        setFailed(true);
      }
    }

    setLoading(false);
  }, [bookingId, error]);

  useEffect(() => {
    let paymentDataRes = null;

    try {
      paymentDataRes = JSON.parse(sessionStorage.getItem(`pdr_${bookingId}`));
    } catch (err) {
      console.error("Error parsing payment data. Error: ", err);
    }

    if (paymentDataRes) {
      setPaymentData(paymentDataRes);
    } else {
      fetchData();
    }
  }, [fetchData, bookingId]);

  useEffect(() => {
    if (paymentData) {
      sessionStorage.setItem(`pdr_${bookingId}`, JSON.stringify(paymentData));
    }
  }, [paymentData, bookingId]);

  return (
    <>
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <Laoder />
        </div>
      )}
      {!loading && (error || failed) && <ErrorScreen />}
      {!loading && !error && !failed && paymentData && (
        <SuccessScreen data={paymentData} bookingId={bookingId} />
      )}
    </>
  );
}

function SuccessScreen({ data, bookingId }) {
  const { park, pnr, bookedOn, customer } = data;

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    try {
      if(!dateString) return ""

      const date = parse(dateString, 'yyyy-MM-dd hh:mm:ss a', new Date());
      return format(date, "EEE, dd MMM, yyyy");
    } catch (error) {
      console.log({dateParseError:error})
      return ""
    }
  };

  return (
    <>
      <Breadcrumbs activeId={5} />

      <div className="container py-5 text-center mb-5">
        {/* Success Icon + Title */}
        <div className="mb-4">
          <div className="text-success fs-1">
            <img src={checkIcon} alt="green-circle-check" />
          </div>
          <h4 className="fw-bold text-success">Congratulations</h4>
          <p className="text-muted mb-5">
            Hi {customer?.first_name} {customer?.last_name}! Your payment 
            has been successfully completed, and your ticket is confirmed.
          </p>
        </div>

        {/* Main Card */}
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row g-4">
              {/* Left Column */}
              <div className="col-md-6">
                <div
                  className="p-3 rounded mb-3 text-start"
                  style={{ backgroundColor: "#EDF6ED" }}
                >
                  <p className="mb-2 fw-semibold">
                    We have successfully completed your booking. You will
                    receive a
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li>
                      <span>•</span> Confirmation SMS on{" "}
                      <strong className="text-success">
                        {customer?.phone}
                      </strong>
                    </li>
                    <li>
                      <span>•</span> Email at{" "}
                      <strong className="text-success">
                        {customer?.email}
                      </strong>
                    </li>
                  </ul>
                </div>

                <div className="text-start">
                   <h5 className="fw-semibold mt-5">Please Remember</h5>
                  <ul className="ps-3">
                    <li>Show your <b>SMS or PDF ticket</b> at the counter for entry</li>
                    <li>Arrive <b>at least 20 minutes</b> before your scheduled visit</li>
                  </ul>
                  <h6 className="fw-semibold">Helpful Tips</h6>
                  <ul className="ps-3">
                    <li>Check traffic conditions to reach the park on time</li>
                    <li>Stay aware of your surroundings</li>
                    <li>Keep your valuables safe</li>
                    <li>Inform family or friends about your travel plan</li>
                  </ul>
                </div>
              </div>

              {/* Right Column */}
              <div className="col-md-6 text-start">
                <div
                  className="rounded p-3 mb-3"
                  style={{ backgroundColor: "#F3F3F3" }}
                >
                  <h6 className="fw-bold text-muted mb-3">TICKET DETAILS</h6>
                  <p className="mb-1 d-flex justify-content-between">
                    <span>Order ID:</span>
                    <strong>{pnr}</strong>
                  </p>
                  <p className="mb-1 d-flex justify-content-between">
                    <span>Park:</span>
                    <strong>{park}</strong>
                  </p>
                  <p className="mb-1 d-flex justify-content-between">
                    <span>Branch:</span>
                    <strong>{data?.branch}</strong>
                  </p>
                  <p className="mb-3 d-flex justify-content-between">
                    <span>Date:</span>
                    <strong>{formatDate(bookedOn)}</strong>
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-success w-100 mb-5"
                  onClick={() => navigate(`/ticket-view/${bookingId}`)}
                >
                  VIEW TICKET
                </button>
                <div className="text-center">
                  <Link
                    to="/"
                    className="text-success fw-bold text-decoration-none"
                  >
                    GO BACK TO HOME
                  </Link>
                </div>

                {/* Terms */}
                <div className="mt-4">
                  <h5 className="fw-semibold">Terms & Condition</h5>
                  <ul className="ps-3">
                    <li>All tickets are <b>non-exchangeable, non-cancelable, and non-refundable</b></li>
                    <li>Outside food is <b>not allowed</b> inside the park</li>
                    <li>All standard <b>ticket conditions apply</b></li>
                    <li>Children must be <b>under {`4'3"`}</b> to qualify for child tickets</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ErrorScreen() {
  const navigate = useNavigate()
  return (
    <div className="d-flex flex-column gap-2" style={{height: '80dvh'}}>
      <div>
        <Header />
      </div>
      <div className="top-warning-card bg_danger d-flex flex-column flex-md-row align-items-center gap-2">
        <div>
          <img
            src={nfcPayment}
            style={{ position: "relative", top: "-20px" }}
            className="nfc-payment-icon"
            alt="download"
          />
        </div>
        <div className="px-md-0 px-2">
          <div className="fw_600 fs_32 text-light">Sorry!</div>
          <div>
            <span className="fw_400 fs_14 text-light">
              There must have been some issue with your payment.
            </span>
          </div>
        </div>
      </div>
      <div className="main-error-messages d-flex flex-column flex-md-row gap-2 px-5">
        <div className="w-50 flex-col-w-50">
          <div className="payment-unsuccessful-msg fw_350 fs_16 text_danger">
            Payment Unsuccessful!
          </div>
          <div className="could-not-process-error fw_350 fs_24 text_danger">
            We could not process your booking!
          </div>
          <div className="fw_350 fs_16">
            <span>
              Your payment was not received.
            </span>
          </div>
          <div>
            <button 
              type="button" 
              className="btn btn-danger mt-3"
              onClick={() => navigate(`/`)}
            >
              Back to Park Home Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
